import React from "react"
import { ConsumptionType } from "@/api/api-client"
import { Toaster } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { GoogleOAuthProvider } from "@react-oauth/google"
import { AuthProvider, UserType, useAuth } from "./hooks/useAuth"
import { CartProvider } from "./hooks/useCart"
import { BrandThemeProvider } from "./hooks/useBrandTheme"
import ProtectedRoute from "./components/ProtectedRoute"
import { HelmetProvider } from "react-helmet-async"

// Marketing / Static Pages
import Index from "./pages/marketing/Index"
import AboutUs from "./pages/marketing/AboutUs"
import Services from "./pages/marketing/Services"
import Contact from "./pages/marketing/Contact"
import Requirements from "./pages/marketing/Requirements"
import NotFound from "./pages/marketing/NotFound"

// Auth Pages
import Login from "./pages/auth/Login"
import BuyerSignup from "./pages/auth/BuyerSignup"
import VerifyEmail from "./pages/auth/VerifyEmail"

// Catalog Pages
import ProductMarketplace from "./pages/catalog/ProductMarketplace"
import ProductDetail from "./pages/catalog/ProductDetail"
import Checkout from "./pages/catalog/Checkout"

// Profile Pages
import SavedAddresses from "./pages/profile/SavedAddresses"

// Dashboard Pages

// Order Pages
import OrderTracking from "./pages/orders/OrderTracking"
import SupplierOrderTracking from "./pages/orders/SupplierOrderTracking"
import VendorDashboard from "./pages/dashboard/VendorDashboard"
import AdminDashboard from "./pages/dashboard/AdminDashboard"

const queryClient = new QueryClient()

const SoftLaunchGuard = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth()
  const isSoftLaunch = import.meta.env.VITE_SOFT_LAUNCH === "true"

  if (loading) return null

  if (isSoftLaunch) {
    if (user?.userType === UserType.Admin) {
      return <>{children}</>
    }
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrandThemeProvider>
              <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ""}>
                <AuthProvider>
                  <CartProvider>
                    <TooltipProvider>
                      <Toaster />
                      <BrowserRouter>
                        <Routes>
                          <Route path="/" element={<Index />} />
                          <Route path="/login" element={<Login />} />
                          <Route path="/buyer-signup" element={<BuyerSignup />} />
                          <Route path="/verify-email" element={<VerifyEmail />} />

                          {/* Buyer Catalog Routes */}
                          <Route
                            path="/catalog/industrial"
                            element={
                              <SoftLaunchGuard>
                                <ProductMarketplace consumptionType={ConsumptionType.Industrial} />
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/catalog/human_consumption"
                            element={
                              <SoftLaunchGuard>
                                <ProductMarketplace consumptionType={ConsumptionType.Human} />
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/catalog/marketplace"
                            element={
                              <SoftLaunchGuard>
                                <ProductMarketplace />
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/catalog/product/:id"
                            element={
                              <SoftLaunchGuard>
                                <ProductDetail />
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/catalog/api"
                            element={
                              <SoftLaunchGuard>
                                <ProductMarketplace />
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/checkout"
                            element={
                              <SoftLaunchGuard>
                                <ProtectedRoute
                                  allowedRoles={[UserType.Buyer, UserType.Vendor, UserType.Admin]}
                                >
                                  <Checkout />
                                </ProtectedRoute>
                              </SoftLaunchGuard>
                            }
                          />
                          <Route
                            path="/profile/addresses"
                            element={
                              <SoftLaunchGuard>
                                <ProtectedRoute
                                  allowedRoles={[UserType.Buyer, UserType.Vendor, UserType.Admin]}
                                >
                                  <SavedAddresses />
                                </ProtectedRoute>
                              </SoftLaunchGuard>
                            }
                          />

                          {/* Seller Dashboard */}
                          <Route
                            path="/dashboard-vendor"
                            element={
                              <SoftLaunchGuard>
                                <ProtectedRoute allowedRoles={[UserType.Vendor, UserType.Admin]}>
                                  <VendorDashboard />
                                </ProtectedRoute>
                              </SoftLaunchGuard>
                            }
                          />

                          {/* Admin Dashboard */}
                          <Route
                            path="/dashboard-admin"
                            element={
                              <ProtectedRoute allowedRoles={[UserType.Admin]}>
                                <AdminDashboard />
                              </ProtectedRoute>
                            }
                          />

                          {/* Admin Routes */}
                          <Route
                            path="/order-tracking"
                            element={
                              <SoftLaunchGuard>
                                <ProtectedRoute
                                  allowedRoles={[UserType.Admin, UserType.Vendor, UserType.Buyer]}
                                >
                                  <OrderTracking />
                                </ProtectedRoute>
                              </SoftLaunchGuard>
                            }
                          />
                          {/* Public Pages */}
                          <Route path="/about" element={<AboutUs />} />
                          <Route path="/services" element={<Services />} />
                          <Route path="/contact" element={<Contact />} />
                          <Route path="/requirements" element={<Requirements />} />

                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </BrowserRouter>
                    </TooltipProvider>
                  </CartProvider>
                </AuthProvider>
              </GoogleOAuthProvider>
            </BrandThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  )
}
export default App

